import { Link, useParams } from 'react-router';
import { Boxes, Info, Lock } from 'lucide-react';
import { ApiError } from '../../api/client';
import { useTarget } from '../../api/queries';
import { AddToMenuButton } from '../../components/AddToMenuButton';
import { CopyButton } from '../../components/CopyButton';
import { ErrorAlert } from '../../components/ErrorAlert';
import { IconChip, PageHeader, Section } from '../../components/PageHeader';
import { PageSkeleton } from '../../components/Skeleton';
import { StateDimensions } from '../../components/StateDimensions';
import { StatusDot } from '../../components/StatusBadge';
import { TabBar, useTab, type TabDef } from '../../components/Tabs';
import { VisitButton } from '../../components/VisitButton';
import NotFoundPage from '../NotFoundPage';
import { BackupsTab } from './BackupsTab';
import { DataTab } from './DataTab';
import { EndpointsTable } from './EndpointsTable';
import { LogsTab } from './LogsTab';
import { PendingConfigBanner, TargetActions } from './TargetActions';
import { CurrentRelease, ServicesTable } from './TargetDetails';
import { TargetJobsTab } from './TargetJobsTab';

export default function TargetPage() {
  const { id = '' } = useParams();
  const q = useTarget(id);
  const tabs: TabDef[] = [
    { id: 'logs', label: 'Logs' },
    { id: 'backups', label: 'Backups' },
    { id: 'data', label: 'Volumes & databases' },
    { id: 'jobs', label: 'Jobs' },
  ];
  const tab = useTab(tabs);

  if (q.isPending) return <PageSkeleton />;
  if (q.isError) {
    if (q.error instanceof ApiError && q.error.status === 404) return <NotFoundPage />;
    return <ErrorAlert error={q.error} onRetry={() => void q.refetch()} />;
  }
  const t = q.data;

  return (
    <>
      <PageHeader
        icon={<IconChip icon={Boxes} tone={t.removedAt ? 'neutral' : 'primary'} size="lg" />}
        crumbs={[
          { label: 'Projects', to: '/projects' },
          { label: t.project.key, to: `/projects/${t.project.key}` },
          { label: t.environment },
        ]}
        title={
          <>
            {t.project.name} <span className="text-base-content/50">·</span> {t.environment}
          </>
        }
        actions={
          <div className="flex items-center gap-2">
            <AddToMenuButton target={t} />
            {t.endpoints.length > 0 ? <VisitButton target={t} /> : null}
          </div>
        }
        badges={
          <>
            {t.environmentProtected ? (
              <span className="badge badge-soft badge-warning gap-1">
                <Lock className="size-3" aria-hidden="true" /> protected
              </span>
            ) : null}
            {t.removedAt ? <span className="badge badge-ghost gap-1">application removed</span> : null}
          </>
        }
        subtitle={
          <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>
              on{' '}
              <Link className="link" to={`/vps/${t.vps.id}`}>
                {t.vps.name}
              </Link>{' '}
              ({t.vps.host})
            </span>
            <StatusDot kind="connection" value={t.vps.connection} />
            <span className="inline-flex items-center gap-1">
              stack <code className="font-mono">{t.stack}</code>
              <CopyButton value={t.stack} label="Copy stack name" />
            </span>
          </span>
        }
      />

      <div className="space-y-6">
        <TargetActions target={t} />
        {t.state.observationNote ? (
          <div role="status" className="alert alert-info alert-soft text-sm">
            <Info className="size-4 shrink-0" aria-hidden="true" />
            <span>{t.state.observationNote}</span>
          </div>
        ) : null}
        <PendingConfigBanner target={t} />
        {t.activeJob ? (
          <div role="status" className="alert alert-info alert-soft text-sm">
            <span className="loading loading-spinner loading-sm shrink-0" aria-hidden="true" />
            <span>
              A {t.activeJob.kind} job is {t.activeJob.state.replace('_', ' ')}
              {t.activeJob.stage ? ` (${t.activeJob.stage})` : ''}. Other actions on this target wait until it finishes.{' '}
              <Link className="link" to={`/jobs/${t.activeJob.id}`}>
                Follow the job
              </Link>
            </span>
          </div>
        ) : null}

        <Section title="State" description="Process, health, endpoint readiness and observation time are separate: an application can run while unhealthy, and unknown is not stopped.">
          <StateDimensions state={t.state} endpoints={t.endpoints} />
        </Section>

        <Section title="Endpoints" description="HTTPS is reported ready only when DNS resolves to the server and the certificate is valid.">
          <EndpointsTable endpoints={t.endpoints} stale={t.state.stale} />
        </Section>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <Section title="Services">
            <ServicesTable target={t} />
          </Section>
          <Section title="Current release">
            <CurrentRelease target={t} />
          </Section>
        </div>

        <div>
          <TabBar tabs={tabs} active={tab} label="Target sections" />
          <div key={tab} className="mt-4 animate-rise" role="tabpanel">
            {tab === 'logs' ? <LogsTab target={t} /> : null}
            {tab === 'backups' ? <BackupsTab target={t} /> : null}
            {tab === 'data' ? <DataTab target={t} /> : null}
            {tab === 'jobs' ? <TargetJobsTab targetId={t.id} /> : null}
          </div>
        </div>
      </div>
    </>
  );
}
